import { useState } from 'react';
import { Btn } from '../../../components/admin/console';
import { useUpdateBuildReleaseNotes } from './data/hooks';
import type { Build } from './data/types';

export function ReleaseNotesEditor({ build }: { build: Build }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(build.releaseNotes ?? '');
  const [error, setError] = useState<string | null>(null);
  const mutation = useUpdateBuildReleaseNotes();
  const isPending = mutation.isPending;

  const startEdit = () => {
    setDraft(build.releaseNotes ?? '');
    setError(null);
    setIsEditing(true);
  };

  const cancel = () => {
    setIsEditing(false);
    setError(null);
  };

  const save = () => {
    setError(null);
    mutation.mutate(
      {
        channel: build.channel,
        appDir: build.appDir,
        versionDir: build.versionDir,
        packageId: build.packageId,
        releaseNotes: draft,
      },
      {
        onSuccess: () => setIsEditing(false),
        onError: (err) => setError(err.message || 'Failed to save'),
      },
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
          Release notes
        </div>
        {!isEditing && (
          <Btn size="sm" variant="ghost" onClick={startEdit}>
            Edit
          </Btn>
        )}
      </div>
      {isEditing ? (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={isPending}
            rows={6}
            className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50"
          />
          {error && <div className="text-xs text-red-400">{error}</div>}
          <div className="flex items-center gap-2">
            <Btn size="sm" variant="primary" onClick={save} disabled={isPending}>
              {isPending ? 'Saving…' : 'Save'}
            </Btn>
            <Btn size="sm" variant="ghost" onClick={cancel} disabled={isPending}>
              Cancel
            </Btn>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
          {build.releaseNotes || '—'}
        </p>
      )}
    </div>
  );
}

export default ReleaseNotesEditor;
